import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  User as FirebaseUser,
  deleteUser,
  signInWithEmailAndPassword as firebaseSignIn,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  where,
  getDocs,
  arrayUnion,
  writeBatch,
  type DocumentData,
} from "firebase/firestore";
import { auth, db } from "@/api/firebase/firebase";
import { User, UserRole, AuthError, TransactionData, CohortData, ParticipantData } from "@/types/user";
import { logger } from "@/services/logger";
import { SnapshotService } from "@/services/SnapshotService";


export class AuthService {
  static handleAuthError(error: AuthError): string {
    switch (error.code) {
      case "auth/user-not-found":
        return "No account found with this email address";
      case "auth/wrong-password":
        return "Invalid password";
      case "auth/email-already-in-use":
        return "An account with this email already exists";
      case "auth/weak-password":
        return "Password should be at least 6 characters";
      case "auth/invalid-email":
        return "Invalid email address";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later";
      default:
        return error.message || "An unexpected error occurred";
    }
  }
}

export const loginWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<User> => {
  try {
    // Authenticate with Firebase Auth
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const firebaseUser = userCredential.user;

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

    if (!userDoc.exists()) {
      throw new Error("User profile not found");
    }

    const userData = userDoc.data();
    const user: User = {
      id: firebaseUser.uid,
      email: firebaseUser.email || "",
      name: userData.name || firebaseUser.displayName || "",
      role: userData.role || "user",
      currency: userData.currency,
      numLogins: userData.numLogins || 0,
      activationDate: userData.activationDate,
      lastLoginDate: userData.lastLoginDate,
    };

    // Update login stats — track distinct calendar days in loginHistory (YYYY-MM-DD)
    const todayKey = new Date().toISOString().split("T")[0];
    await updateDoc(doc(db, "users", firebaseUser.uid), {
      numLogins: (user.numLogins || 0) + 1,
      lastLoginDate: Timestamp.fromDate(new Date()),
      loginHistory: arrayUnion(todayKey),
    });

    // Set activation date if first login
    if (!user.activationDate) {
      await updateDoc(doc(db, "users", firebaseUser.uid), {
        activationDate: Timestamp.fromDate(new Date()),
      });
    }

    return user;
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Login failed", authError, "AUTH");
    throw new Error(AuthService.handleAuthError(authError));
  }
};

export const signUpWithEmailAndPassword = async (
  email: string,
  password: string,
  name: string
): Promise<User> => {
  // SECURITY: Role is always "user" - never trust client-provided roles
  // Admin/trainer roles must be assigned via Firebase Console or Cloud Functions
  const role: UserRole = "user";

  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const firebaseUser = userCredential.user;

    // Create user document in Firestore
    const user: User = {
      id: firebaseUser.uid,
      email: firebaseUser.email || "",
      name,
      role,
      numLogins: 0,
      createdAt: new Date(),
    };

    await setDoc(doc(db, "users", firebaseUser.uid), {
      ...user,
      createdAt: Timestamp.fromDate(new Date()),
    });

    return user;
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Signup failed", authError, "AUTH");
    throw new Error(AuthService.handleAuthError(authError));
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Logout failed", authError, "AUTH");
    throw new Error("Failed to logout");
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Password reset failed", authError, "AUTH");
    throw new Error(AuthService.handleAuthError(authError));
  }
};

export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return null;
    }

    const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();

    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || "",
      name: userData.name || firebaseUser.displayName || "",
      role: userData.role || "user",
      currency: userData.currency,
      numLogins: userData.numLogins || 0,
      activationDate: userData.activationDate,
      lastLoginDate: userData.lastLoginDate,
    };
  } catch (error: unknown) {
    logger.error("Failed to get current user", error, "AUTH");
    return null;
  }
};

export const updateUserCurrency = async (currency: string): Promise<void> => {
  try {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error("No authenticated user");
    }

    await updateDoc(doc(db, "users", firebaseUser.uid), {
      currency,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Failed to update currency", authError, "AUTH");
    throw new Error("Failed to update currency preference");
  }
};

export const resetUserAccount = async (): Promise<void> => {
  try {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error("No authenticated user");
    }

    // Get all cohorts the user is part of
    const cohortsSnapshot = await getDocs(collection(db, "cohorts"));
    const userCohorts: string[] = [];

    cohortsSnapshot.forEach((cohortDoc) => {
      const cohortData = cohortDoc.data();
      const isParticipant = cohortData.participants?.some(
        (participant: any) => participant.email?.toLowerCase() === firebaseUser.email?.toLowerCase()
      );
      if (isParticipant) {
        userCohorts.push(cohortDoc.id);
      }
    });

    // Use batch for all writes (max 500 operations per batch)
    const batch = writeBatch(db);

    // Reset user currency in batch
    batch.update(doc(db, "users", firebaseUser.uid), {
      currency: null,
      updatedAt: Timestamp.fromDate(new Date()),
    });

    // Reset user progress for each cohort in batch
    for (const cohortId of userCohorts) {
      const docId = `${firebaseUser.uid}_${cohortId}`;
      const progressDoc = doc(db, "userProgress", docId);
      batch.set(progressDoc, {
        userId: firebaseUser.uid,
        cohortId: cohortId,
        currentTransactionIndex: 0,
        accounts: [],
        liabilities: [],
        equityAccounts: [],
        equityAmounts: [],
        accountDroppedAmounts: {},
        liabilityDroppedAmounts: {},
        equityAccountDroppedAmounts: {},
        expenseAccounts: [],
        incomeAccounts: [],
        expenseDroppedAmounts: {},
        incomeDroppedAmounts: {},
        lastUpdated: Timestamp.now(),
      });
    }

    // Commit all progress resets in one batch
    await batch.commit();

    // Clear transaction snapshots in parallel (each uses its own batch internally)
    await Promise.all(
      userCohorts.map((cohortId) =>
        SnapshotService.deleteSnapshots({
          userId: firebaseUser.uid,
          cohortId: cohortId,
        })
      )
    );
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Failed to reset account", authError, "AUTH");
    throw new Error("Failed to reset account");
  }
};

// Get transactions for the current user from their cohorts
/**
 * @param viewModeOverride Forces the transaction set to load. Without it the
 * cohort's own `transactionViewMode` decides, which is the trainer's setting
 * for everyone; participants who switched views pass their own mode here.
 */
export const getUserTransactions = async (
  viewModeOverride?: "default" | "custom"
): Promise<TransactionData[]> => {
  try {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error("No authenticated user");
    }

    // Get all cohorts
    const cohortsSnapshot = await getDocs(collection(db, "cohorts"));

    // A user can be left behind in an old cohort's participants list (e.g. moved
    // from Cohort 1 to Cohort 2). Merging transactions from every matching cohort
    // duplicates every transaction, so resolve a single cohort — the most recently
    // created one — matching CohortService.getCohortByParticipantEmail, which the
    // rest of the app (progress, snapshots) uses to pick the user's cohort.
    let currentCohortId: string | null = null;
    let currentCohortData: DocumentData | null = null;

    cohortsSnapshot.forEach((cohortDoc) => {
      const cohortData = cohortDoc.data();

      // Check if the current user is a participant in this cohort
      const isParticipant = cohortData.participants?.some(
        (participant: ParticipantData) => participant.email?.toLowerCase() === firebaseUser.email?.toLowerCase()
      );

      if (!isParticipant) return;

      const createdAt = cohortData.createdAt?.toMillis?.() ?? 0;
      const currentCreatedAt = currentCohortData?.createdAt?.toMillis?.() ?? 0;
      if (!currentCohortData || createdAt > currentCreatedAt) {
        currentCohortId = cohortDoc.id;
        currentCohortData = cohortData;
      }
    });

    if (!currentCohortId || !currentCohortData) {
      return [];
    }

    const cohortData: DocumentData = currentCohortData;
    let transactions = [];

    // First check the transaction view mode
    const viewMode =
      viewModeOverride || cohortData.transactionViewMode || "default";

    // Load transactions based on view mode
    if (
      viewMode === "custom" &&
      cohortData.transactionConfig?.custom?.transactions?.length
    ) {
      transactions = cohortData.transactionConfig.custom.transactions;
    } else {
      // For "default" view mode or if custom transactions aren't available
      transactions = cohortData.transactionConfig?.default?.transactions || [];
    }

    // Add cohort info to each transaction and use incrementing index
    return transactions.map(
      (transaction: TransactionData, index: number) => ({
        ...transaction,
        id: index + 1, // Use incrementing index instead of raw ID
        cohortId: currentCohortId,
        cohortName: cohortData.name,
      })
    );
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Failed to get user transactions", authError, "AUTH");
    throw new Error("Failed to get user transactions");
  }
};

/**
 * Get transactions for a specific cohort by ID (used when trainers join a course).
 */
export const getTransactionsByCohortId = async (
  cohortId: string,
  viewModeOverride?: "default" | "custom"
): Promise<TransactionData[]> => {
  try {
    const cohortDoc = await getDoc(doc(db, "cohorts", cohortId));
    if (!cohortDoc.exists()) return [];

    const cohortData = cohortDoc.data();
    const viewMode =
      viewModeOverride || cohortData.transactionViewMode || "default";

    let transactions = [];
    if (
      viewMode === "custom" &&
      cohortData.transactionConfig?.custom?.transactions?.length
    ) {
      transactions = cohortData.transactionConfig.custom.transactions;
    } else {
      transactions =
        cohortData.transactionConfig?.default?.transactions || [];
    }

    return transactions.map(
      (transaction: TransactionData, index: number) => ({
        ...transaction,
        id: index + 1,
        cohortId: cohortDoc.id,
        cohortName: cohortData.name,
      })
    );
  } catch (error: unknown) {
    logger.error("Failed to get cohort transactions", error, "AUTH");
    throw new Error("Failed to get cohort transactions");
  }
};

export const createParticipantAccount = async (
  email: string,
  name: string,
  cohortId: string
): Promise<{ alreadyHasAccount: boolean }> => {
  try {
    // Check if user already exists
    const usersQuery = query(
      collection(db, "users"),
      where("email", "==", email)
    );
    const userSnapshot = await getDocs(usersQuery);

    if (!userSnapshot.empty) {
      // User exists, add them to the cohort
      const userDoc = userSnapshot.docs[0];
      const userData = userDoc.data();
      await updateDoc(doc(db, "users", userDoc.id), {
        cohorts: arrayUnion(cohortId),
        emailLowercase: email.toLowerCase(),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // Check if user already has an active account (trainer, admin, or password already set)
      const alreadyHasAccount =
        userData.role === "trainer" ||
        userData.role === "admin" ||
        userData.isPasswordSet !== false;

      return { alreadyHasAccount };
    } else {
      // Create a temporary user document
      // Note: We don't send a password reset email here because the user doesn't have
      // a Firebase Auth account yet. The admin can share the setup-password link manually,
      // or we can implement a separate email notification system.
      const tempUserDoc = doc(collection(db, "users"));
      await setDoc(tempUserDoc, {
        email,
        emailLowercase: email.toLowerCase(),
        name,
        role: "user",
        cohorts: [cohortId],
        isPasswordSet: false,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      logger.info(
        "Participant account created",
        {
          email,
          setupUrl: `${window.location.origin}/setup-password?email=${encodeURIComponent(email)}`,
        },
        "AUTH"
      );

      return { alreadyHasAccount: false };
    }
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Failed to create participant account", authError, "AUTH");
    throw new Error(AuthService.handleAuthError(authError));
  }
};

/**
 * Create a user doc for a trainer so they can set up their password and log in.
 * Similar to createParticipantAccount but with role "trainer".
 */
export const createTrainerUserAccount = async (
  email: string,
  name: string
): Promise<{ alreadyHasAccount: boolean }> => {
  try {
    // Check if user already exists
    const usersQuery = query(
      collection(db, "users"),
      where("email", "==", email)
    );
    const userSnapshot = await getDocs(usersQuery);

    if (!userSnapshot.empty) {
      const userData = userSnapshot.docs[0].data();
      const alreadyHasAccount = userData.isPasswordSet !== false;

      // Update role to trainer if not already
      if (userData.role !== "admin") {
        await updateDoc(doc(db, "users", userSnapshot.docs[0].id), {
          role: "trainer",
          updatedAt: Timestamp.fromDate(new Date()),
        });
      }

      return { alreadyHasAccount };
    } else {
      // Create a temporary user document for the trainer
      const tempUserDoc = doc(collection(db, "users"));
      await setDoc(tempUserDoc, {
        email,
        emailLowercase: email.toLowerCase(),
        name,
        role: "trainer",
        cohorts: [],
        isPasswordSet: false,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      return { alreadyHasAccount: false };
    }
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Failed to create trainer user account", authError, "AUTH");
    throw new Error(AuthService.handleAuthError(authError));
  }
};

/**
 * Find user docs by email, ignoring case/whitespace. Firestore equality is
 * case-sensitive, so check the lowercase email, the email as given, and the
 * emailLowercase field (not all docs have it).
 */
const findUserDocsByEmail = async (email: string) => {
  const trimmed = email.trim();
  const normalized = trimmed.toLowerCase();
  const usersRef = collection(db, "users");
  const snapshots = await Promise.all([
    getDocs(query(usersRef, where("email", "==", normalized))),
    getDocs(query(usersRef, where("emailLowercase", "==", normalized))),
    ...(trimmed !== normalized
      ? [getDocs(query(usersRef, where("email", "==", trimmed)))]
      : []),
  ]);

  const seen = new Set<string>();
  return snapshots
    .flatMap((s) => s.docs)
    .filter((d) => (seen.has(d.id) ? false : (seen.add(d.id), true)));
};

/**
 * Check if a user has set up their password (has an active account).
 */
export const checkAccountStatus = async (
  email: string
): Promise<{ exists: boolean; isPasswordSet: boolean; role: string | null }> => {
  try {
    const userDocs = await findUserDocsByEmail(email);

    if (userDocs.length === 0) {
      return { exists: false, isPasswordSet: false, role: null };
    }

    // Prefer an activated account when duplicates exist for the same email
    const userData = (
      userDocs.find((d) => d.data().isPasswordSet !== false) || userDocs[0]
    ).data();
    return {
      exists: true,
      isPasswordSet: userData.isPasswordSet !== false,
      role: userData.role || null,
    };
  } catch (error) {
    logger.error("Failed to check account status", error, "AUTH");
    return { exists: false, isPasswordSet: false, role: null };
  }
};

export const deleteUserAccount = async (email: string): Promise<void> => {
  try {
    // First, find the user document in Firestore
    const usersQuery = query(
      collection(db, "users"),
      where("email", "==", email)
    );
    const userSnapshot = await getDocs(usersQuery);

    if (userSnapshot.empty) {
      throw new Error("User not found");
    }

    const userDoc = userSnapshot.docs[0];

    // Delete the user document from Firestore
    await deleteDoc(doc(db, "users", userDoc.id));

    // Delete user progress documents
    const userProgressQuery = query(
      collection(db, "userProgress"),
      where("userId", "==", userDoc.id)
    );
    const progressSnapshot = await getDocs(userProgressQuery);
    const progressDeletions = progressSnapshot.docs.map((doc) =>
      deleteDoc(doc.ref)
    );
    await Promise.all(progressDeletions);

    // Delete from Firebase Auth
    // Get all users from Firebase Auth (we need to do this in the backend)
    // For now, we'll show an error message that manual deletion is required
    logger.warn(
      "IMPORTANT: Please delete this user from Firebase Authentication console manually",
      { email },
      "AUTH"
    );

    // Log steps to delete from Firebase Auth
    logger.info(
      "Steps to delete from Firebase Auth",
      {
        steps: [
          "1. Go to Firebase Console",
          "2. Select Authentication",
          `3. Find the user with email: ${email}`,
          "4. Click the three dots (...) menu",
          "5. Select 'Delete Account'"
        ]
      },
      "AUTH"
    );
  } catch (error: unknown) {
    const authError = error as AuthError;
    logger.error("Failed to delete user account", authError, "AUTH");
    throw new Error(AuthService.handleAuthError(authError));
  }
};
